import { useEffect, useRef } from "react"
import { usePathname } from "next/navigation"
import { MEDIA_QUERIES } from "@/constants/ui"
import { useMediaQuery } from "@/hooks/use-media-query"
import { cn } from "cn"
import { AnimatePresence, motion } from "motion/react"
import { useInView } from "react-intersection-observer"

import { Button } from "@/components/ui/button"

const BUSINESS_LINKS = [
  { href: "/business", label: "Overview" },
  { href: "/business/how-it-works", label: "How It Works" },
  { href: "#contact", label: "Contact" },
  { href: "/business/packages", label: "Packages" },
]

const AI_SOLUTIONS_LINKS = [
  { href: "/business/destinations", label: "Destinations" },
  { href: "/business/hotels", label: "Hotels" },
]

export function BusinessSubNav() {
  const pathname = usePathname()
  const isXS = useMediaQuery(MEDIA_QUERIES.xsMax)
  const { inView, ref } = useInView()
  const offset = isXS ? 700 : 800
  const { inView: contactInView, ref: contactRef } = useInView({
    rootMargin: `${offset}px 0px -${offset}px 0px`,
  })
  const navRef = useRef<HTMLElement>(null)
  const activeButtonRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  useEffect(() => {
    const contactSection = document.getElementById("contact")
    if (contactSection) {
      contactRef(contactSection)
    }
    return () => contactRef(null)
  }, [contactRef])

  useEffect(() => {
    const activeButton = activeButtonRefs.current.get(pathname)
    if (activeButton && navRef.current) {
      const nav = navRef.current
      const buttonRect = activeButton.getBoundingClientRect()
      const navRect = nav.getBoundingClientRect()

      const isOutOfView =
        buttonRect.left < navRect.left || buttonRect.right > navRect.right

      if (isOutOfView) {
        const buttonCenter =
          activeButton.offsetLeft + activeButton.offsetWidth / 2
        const navCenter = nav.offsetWidth / 2
        nav.scrollTo({ left: buttonCenter - navCenter })
      }
    }
  }, [pathname, inView, contactInView])

  return (
    <>
      <div ref={ref} className="absolute top-[80vh]" />
      <AnimatePresence>
        {!inView && !contactInView && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="pointer-events-none fixed inset-x-0 top-16 z-2 flex justify-center md:top-18"
          >
            <nav
              ref={navRef}
              className="pointer-events-auto scrollbar-hidden flex items-center divide-x overflow-x-auto bg-background pt-2.5 pb-3 shadow-md *:shrink-0 md:rounded-full md:pt-4 md:pb-4.5 md:shadow-xl"
            >
              <ul className="flex items-baseline pr-3 pl-1.5 sm:pl-4 lg:pr-5 lg:pl-9">
                <li>
                  <h3 className="px-2.5 text-2xs font-semibold text-muted">
                    Business
                  </h3>
                </li>
                {BUSINESS_LINKS.map(({ href, label }) => {
                  const isActive = pathname === href
                  return (
                    <li key={href}>
                      <div
                        ref={(el) => {
                          if (el && isActive) {
                            activeButtonRefs.current.set(href, el)
                          }
                        }}
                        className="relative"
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          href={href}
                          className={cn(
                            "px-2.5",
                            isActive && "hover:bg-transparent"
                          )}
                          onClick={(e) => {
                            if (href.startsWith("#")) {
                              e.preventDefault()
                              document
                                .getElementById(href.slice(1))
                                ?.scrollIntoView({
                                  behavior: "smooth",
                                })
                            }
                          }}
                        >
                          {label}
                        </Button>
                        {isActive && (
                          <div className="absolute bottom-0 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-foreground" />
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
              <ul className="flex items-baseline pr-1.5 pl-3 sm:pr-4 lg:pr-9 lg:pl-5">
                <li>
                  <h3 className="px-2.5 text-2xs font-semibold text-muted">
                    AI solutions for
                  </h3>
                </li>
                {AI_SOLUTIONS_LINKS.map(({ href, label }) => {
                  const isActive = pathname === href
                  return (
                    <li key={href}>
                      <div
                        ref={(el) => {
                          if (el && isActive) {
                            activeButtonRefs.current.set(href, el)
                          }
                        }}
                        className="relative"
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          href={href}
                          className={cn(
                            "px-2.5",
                            isActive && "hover:bg-transparent"
                          )}
                        >
                          {label}
                        </Button>
                        {isActive && (
                          <div className="absolute bottom-0 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-foreground" />
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
